/*
 * File: BusinessListItem.tsx
 * Project: beaker
 * -----
 * Last Modified: 11 Aug 2022 4:57:21 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Avatar, Card, Col, List, Space, Spin, Typography, message } from 'antd';
import { Business } from 'models/Business';
import { Users } from 'models/Users';
import { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chuckieSueAddressesApi } from 'redux/services/chuckieSue';
import { useLazyGetAddressesQuery } from 'redux/services/chuckieSue/addressesApi';
import { setCompany, setCompanyName } from 'redux/slices/appSlice';
import { addBusinessAddressQuantity } from 'redux/slices/businessListSlice';
import { setselectedBusiness } from 'redux/slices/selectedDivisionApplicationsSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { InlineStylesModel } from '../../models/InlineStylesModel';

const styles: InlineStylesModel = {
  wrapper: {
    backgroundColor: 'white',
    paddingLeft: 10,
    borderRadius: 5,
    boxShadow: '0 0 4px #8181811a',
    marginBottom: 5,
    cursor: 'pointer'
  },
  wrapperHovered: {
    backgroundColor: 'rgba(0,0,0,.1)',
    paddingLeft: 15,
    borderRadius: 5,
    boxShadow: '0 0 4px #8181811a',
    marginBottom: 5,
    cursor: 'pointer'
  },
  container: {
    alignItems: 'center'
  },
  title: {
    fontWeight: '100',
    marginBottom: 0
  },
  altTitle: {
    fontWeight: '100',
    color: '#18a799',
    marginBottom: 0
  }
};

interface Props {
  business: Business;
  userData?: Users;
}

export const BusinessListItem: FC<Props> = ({ business }) => {
  /* ******************** Hooks ******************** */
  const { chuckieSueAddressesParams } = useAppSelector((state) => state);
  const { cachedAddressQuantity } = useAppSelector((state) => state.businessList);
  const { pathname } = useLocation();
  const isRecycleBin = pathname?.includes('/recycle-bin');

  const dispatch = useAppDispatch();
  const nav = useNavigate();

  const [getLazyAddressQty, { isLoading, isFetching }] = useLazyGetAddressesQuery();

  const isRefetching = Boolean(isLoading || isFetching);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!business.id) return;
      try {
        const response = await getLazyAddressQty({
          bussinessId: business.id,
          params: isRecycleBin ? { ...chuckieSueAddressesParams, returnDeletedDataOnly: true } : chuckieSueAddressesParams
        }).unwrap();

        dispatch(addBusinessAddressQuantity({ key: business.id, value: response.totalCount }));
      } catch (err) {
        console.log(err);
        message.error((err as { data: { errorMessage: string } }).data.errorMessage);
      }
    };

    if (cachedAddressQuantity[business.id] === undefined) fetchData();
  }, [business.id, cachedAddressQuantity]);

  /* ******************** Functions ******************** */

  const handleClick = () => {
    dispatch(setCompany(business.id));
    dispatch(setCompanyName(business.name));
    dispatch(setselectedBusiness(business));
    dispatch(chuckieSueAddressesApi.util.invalidateTags(['Addresses', 'Address']));
    if (!pathname.includes('recycle-bin')) nav(`/business/${business.id}`);
  };

  return (
    <Col span={24} style={{ marginBottom: 5 }}>
      <Card bodyStyle={{ padding: '10px 12px' }} onClick={handleClick} hoverable>
        <List.Item.Meta
          avatar={<Avatar src={business.logoUrl} />}
          title={
            <Space>
              <Typography.Title level={5} style={styles.title}>
                {business.name}
              </Typography.Title>
              <Typography.Title level={5} type="secondary" style={styles.title}>
                |
              </Typography.Title>
              <Typography.Title level={5} type="secondary" style={styles.altTitle}>
                {business.erpId}
              </Typography.Title>
              <Typography.Title level={5} type="secondary" style={styles.title}>
                |
              </Typography.Title>
              <Spin spinning={isRefetching}>
                <Typography.Title level={5} type="secondary" style={styles.altTitle}>
                  {isRefetching ? 'Loading..' : isRecycleBin ? `${cachedAddressQuantity[business.id]} Deleted Addresses` : `${cachedAddressQuantity[business.id]} Addresses`}
                </Typography.Title>
              </Spin>
            </Space>
          }
          style={styles.container}
        />
      </Card>
    </Col>
  );
};
